import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import axiosInstance from "../../../api/axios";
import "./checkin.css";
import SessionAttendance from "./SessionAttendance";

const QrScanner = lazy(() => import("./QrScanner"));

const errorMessage = error => error.response?.data?.message || "Unable to complete the request. Check your connection and try again.";

export default function Checkin() {
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState("");
  const [event, setEvent] = useState(null);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("meal");
  const [camera, setCamera] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [hackers, setHackers] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const lock = useRef(false);
  const lastScan = useRef({ token: "", time: 0 });
  const searchSequence = useRef(0);
  const current = sessions.find(s => s.id === selected);
  const sessionActive = Boolean(current?.active);
  useEffect(() => { if (!sessionActive) setCamera(false); }, [sessionActive]);

  const load = useCallback(async () => {
    const { data } = await axiosInstance.get("/api/checkin/sessions");
    setSessions(data.sessions);
    setEvent({ year: data.year, cycle: data.cycle });
    setSelected(previous => data.sessions.some(s => s.id === previous) ? previous : data.sessions.find(s => s.active)?.id || "");
  }, []);
  useEffect(() => {
    load().catch(e => setError(errorMessage(e)));
    const timer = setInterval(() => load().catch(() => {}), 15000);
    return () => clearInterval(timer);
  }, [load]);
  useEffect(() => { setCamera(false); setResult(null); lastScan.current = { token: "", time: 0 }; }, [selected]);

  const submit = useCallback(async (identity) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await axiosInstance.post("/api/checkin/scan", { session_id: selected, ...identity });
      setResult(data);
      // A refresh failure must not turn a successful check-in into an error.
      await load().catch(() => {});
    } catch (e) { setError(errorMessage(e)); }
    finally { lock.current = false; setBusy(false); }
  }, [selected, load]);

  const scan = useCallback(value => {
    if (lock.current) return;
    const now = Date.now();
    if (lastScan.current.token === value && now - lastScan.current.time < 5000) return;
    lastScan.current = { token: value, time: now };
    submit({ token: value });
  }, [submit]);
  const cameraFailed = useCallback(message => { setCameraError(message); setCamera(false); }, []);

  async function createSession(e) {
    e.preventDefault(); setSaving(true); setError("");
    try {
      const { data } = await axiosInstance.post("/api/checkin/sessions", { name, kind });
      await load(); setSelected(data.id); setName("");
    } catch (err) { setError(errorMessage(err)); }
    finally { setSaving(false); }
  }
  async function toggleSession() {
    setSaving(true); setError(""); setCamera(false);
    try {
      await axiosInstance.patch(`/api/checkin/sessions/${selected}`, { active: !current.active });
      await load();
    } catch (err) { setError(errorMessage(err)); }
    finally { setSaving(false); }
  }
  async function deleteSession() {
    setSaving(true); setError(""); setCamera(false);
    try {
      await axiosInstance.delete(`/api/checkin/sessions/${selected}`);
      setSessions(previous => previous.filter(session => session.id !== selected));
      setSelected(""); setResult(null);
      await load();
    } catch (err) { setError(errorMessage(err)); }
    finally { setSaving(false); }
  }
  async function search(e) {
    e.preventDefault(); setError(""); setSearching(true); setHackers([]); setSearched(false);
    const sequence = ++searchSequence.current;
    try {
      const { data } = await axiosInstance.get("/api/checkin/hackers", { params: { q: query.trim() } });
      if (sequence === searchSequence.current) { setHackers(data.hackers); setSearched(true); }
    } catch (err) { if (sequence === searchSequence.current) setError(errorMessage(err)); }
    finally { if (sequence === searchSequence.current) setSearching(false); }
  }

  return <main className="checkin-page">
    <header><p className="checkin-eyebrow">ADMIN · {event ? `${event.cycle} ${event.year}` : "HACKPRINCETON"}</p>
      <h1>Check-in & Meals</h1><p>Choose a session, then scan each hacker’s pass. Each hacker can check in once per session.</p></header>
    {error && <div role="alert" className="checkin-feedback checkin-feedback--error">{error}</div>}
    <section className="checkin-panel" aria-labelledby="session-title">
      <h2 id="session-title">Current session</h2>
      <label htmlFor="checkin-session">Scanning for</label>
      <div className="checkin-row">
        <select id="checkin-session" value={selected} disabled={busy || saving} onChange={e => setSelected(e.target.value)}>
          <option value="">Select a session</option>
          {sessions.map(s => <option key={s.id} value={s.id}>{s.name} · {s.count} checked in{s.active ? "" : " · Closed"}</option>)}
        </select>
        {current && <button type="button" disabled={busy || saving} onClick={toggleSession}>{current.active ? "Close session" : "Reopen session"}</button>}
        {current && <button type="button" className="checkin-cancel" disabled={busy || saving} onClick={deleteSession}>Delete session</button>}
      </div>
      {current && <p><strong>{current.count}</strong> {current.kind === "meal" ? "meals claimed" : "hackers arrived"} · {current.active ? "Open" : "Closed"}</p>}
      {!sessions.length && <p>Create your first arrival or meal session below to begin.</p>}
      <details open={!sessions.length || undefined}><summary>Create an arrival or meal session</summary>
        <form onSubmit={createSession} className="checkin-create">
          <label>Session name<input value={name} onChange={e => setName(e.target.value)} placeholder="Friday Dinner" maxLength={80} required /></label>
          <label>Type<select value={kind} onChange={e => setKind(e.target.value)}><option value="meal">Meal</option><option value="arrival">Event arrival</option></select></label>
          <button disabled={saving || busy || !name.trim()}>Create session</button>
        </form>
        <p>Use a separate session for each meal, such as Friday Dinner and Saturday Lunch.</p>
      </details>
    </section>
    <div className="checkin-columns">
      <section className="checkin-panel" aria-labelledby="scanner-title">
        <h2 id="scanner-title">Scan a pass</h2>
        <p>At arrival, check the hacker’s student ID against the name shown.</p>
        <button type="button" disabled={!current?.active || busy} onClick={() => { setCameraError(""); setCamera(value => !value); }}>{camera ? "Stop camera" : "Start camera"}</button>
        {busy && <p role="status">Checking in…</p>}
        {result && <div role={result.duplicate ? "alert" : "status"} className={`checkin-feedback ${result.duplicate ? "checkin-feedback--duplicate" : "checkin-feedback--success"}`}>
          <h3>{result.duplicate ? "Already checked in" : result.kind === "meal" ? "Meal claimed" : "Arrival confirmed"}</h3>
          <strong>{result.hacker.first} {result.hacker.last}</strong><p>{result.hacker.email}</p>
          <p>{result.session} · {new Date(result.checked_in_at).toLocaleString()}</p>
        </div>}
        {camera && current?.active && <Suspense fallback={<p role="status">Loading scanner…</p>}><QrScanner onScan={scan} onError={cameraFailed} /></Suspense>}
        {cameraError && <p role="alert">{cameraError}</p>}

      </section>
      <section className="checkin-panel" aria-labelledby="manual-title">
        <h2 id="manual-title">Manual check-in</h2><p>Find a confirmed hacker if their QR code is unavailable.</p>
        <form onSubmit={search}>
          <label htmlFor="hacker-search">Name or email</label>
          <input id="hacker-search" value={query} minLength={2} maxLength={100} required onChange={e => { setQuery(e.target.value); setHackers([]); setSearched(false); searchSequence.current += 1; setSearching(false); }} />
          <button disabled={searching || query.trim().length < 2}>{searching ? "Searching…" : "Search"}</button>
        </form>
        {searched && !hackers.length && <p>No confirmed hackers found.</p>}
        <ul className="checkin-hackers">{hackers.map(hacker => <li key={hacker.email}>
          <strong>{hacker.first} {hacker.last}</strong><span>{hacker.email}</span><span>{hacker.school}</span>
          <button type="button" disabled={!current?.active || busy} onClick={() => submit({ email: hacker.email })}>Check in to {current?.name || "selected session"}</button>
        </li>)}</ul>
        {hackers.length === 20 && <p>Showing up to 20 matches. Refine your search if needed.</p>}
      </section>
    </div>
    {current && <SessionAttendance key={selected} session={current} disabled={busy || saving}
      onCancelStart={() => setCamera(false)}
      onCancelled={async () => {
        setResult(null); lastScan.current = { token: "", time: 0 };
        await load();
      }} />}
  </main>;
}
